import * as React from 'react';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Button, Link} from '@shopify/polaris';
import {MobileHamburgerMajorMonotone} from '@shopify/polaris-icons';
import {
  NavigationDesktop,
  NavigationMobile,
  LoginModal,
  UserMenu,
} from 'sections/services-marketplace/experts/components';
import {
  Routes,
  fetchTaxonomy,
} from 'sections/services-marketplace/experts/utilities';
import {LoginModalContext} from '../login-modal/LoginModal';
import {TaxonomyName} from '../../utilities/taxonomy';
import {GetShopAuthQueryData} from 'sections/services-marketplace/experts/sections/with-auth/graphql/GetShopAuth.graphql';
import withAuth from 'sections/services-marketplace/experts/sections/with-auth/withAuth';

import Logo from './svg/logo.svg';

import styles from './Header.scss';

interface State {
  navigationExpanded: boolean;
  modalOpen: boolean;
  modalContext?: LoginModalContext;
  modalText?: string;
}

interface OwnProps {
  currentShop: GetShopAuthQueryData.CurrentShop | null;
  currentShopUser: GetShopAuthQueryData.CurrentShopUser | null;
}

type ComposedProps = WithI18nProps & OwnProps;

class Header extends React.PureComponent<ComposedProps, State> {
  state: State = {
    navigationExpanded: false,
    modalOpen: false,
    modalContext: undefined,
    modalText: undefined,
  };

  taxonomy = fetchTaxonomy(TaxonomyName.Navigational).taxonomy;

  toggleNavigation = (): void => {
    this.setState(({navigationExpanded}) => ({
      navigationExpanded: !navigationExpanded,
    }));
  };

  toggleModal = (context?: LoginModalContext) => {
    const {i18n} = this.props;
    const {modalOpen} = this.state;

    return () => {
      this.setState({
        modalOpen: !modalOpen,
        modalContext: context,
        modalText:
          context === LoginModalContext.CreateJob
            ? i18n.translate('Global.PostAJob.modalText')
            : undefined,
      });
    };
  };

  renderLoggedInLinks() {
    const {i18n, currentShop} = this.props;

    return currentShop ? (
      <>
        <li className={styles.ListItem}>
          <a href="/create-a-job" className={styles.ListItemLink}>
            {i18n.translate('Global.Header.navigation.buttonCta')}
          </a>
        </li>
        <li className={styles.ListItem}>
          <a
            href={Routes.jobsPath(currentShop.permanentDomain)}
            className={styles.ListItemLink}
          >
            {i18n.translate('Global.Header.navigation.jobs')}
          </a>
        </li>
        <li className={styles.ListItem}>
          <a
            href={Routes.invoicesPath(currentShop.permanentDomain)}
            className={styles.ListItemLink}
          >
            {i18n.translate('Global.Header.navigation.invoices')}
          </a>
        </li>
      </>
    ) : null;
  }

  renderLoggedOutLinks() {
    const {i18n} = this.props;

    return (
      <>
        <li className={styles.ListItem}>
          <a
            href={Routes.referralUrl('header')}
            className={styles.ExternalListItemLink}
          >
            {i18n.translate('Global.Header.navigation.shopify')}
          </a>
        </li>
        <li className={styles.ListItem}>
          <div className={styles.ListItemButton}>
            <button
              onClick={this.toggleModal(LoginModalContext.Login)}
              type="button"
            >
              {i18n.translate('Global.Header.navigation.login')}
            </button>
          </div>
        </li>
        <li className={styles.ListItem}>
          <Button
            primary
            onClick={this.toggleModal(LoginModalContext.CreateJob)}
          >
            {i18n.translate('Global.Header.navigation.buttonCta')}
          </Button>
        </li>
      </>
    );
  }

  render() {
    const {navigationExpanded, modalOpen, modalContext, modalText} = this.state;
    const {i18n, currentShop} = this.props;

    return (
      <>
        <header className={styles.Header}>
          <div className={styles.ControlBarContainer}>
            <div className={styles.ControlBar}>
              <div className={styles.Logo}>
                <Link url={Routes.homePath()}>
                  <img
                    src={Logo}
                    alt={i18n.translate('Global.Header.logoAlt')}
                  />
                </Link>
              </div>

              <div className={styles.GlobalControls}>
                <nav className={styles.GlobalNavigation}>
                  <ul className={styles.List}>
                    {currentShop
                      ? this.renderLoggedInLinks()
                      : this.renderLoggedOutLinks()}
                  </ul>
                </nav>

                <div className={styles.MenuAndUserControls}>
                  <div className={styles.MenuButton}>
                    <Button
                      accessibilityLabel={i18n.translate(
                        'Global.Header.menuButtonText'
                      )}
                      ariaControls="NavigationDrawer"
                      ariaExpanded={navigationExpanded}
                      onClick={this.toggleNavigation}
                      icon={MobileHamburgerMajorMonotone}
                      plain
                    />
                  </div>
                  {currentShop && <UserMenu />}
                </div>
              </div>
            </div>
          </div>
          <NavigationMobile
            id="NavigationDrawer"
            navItems={this.taxonomy}
            expanded={navigationExpanded}
            onClose={this.toggleNavigation}
            onLoginModalPrompt={this.toggleModal}
          />
          <NavigationDesktop navItems={this.taxonomy} />
        </header>
        <LoginModal
          open={modalOpen}
          context={modalContext}
          modalText={modalText}
          onClose={this.toggleModal()}
        />
      </>
    );
  }
}

export default withAuth(withI18n()(Header));
